import React from 'react';
import { tv } from 'tailwind-variants';
import Tooltip from '@/components/ui/Tooltip';
import Popover from '@/components/ui/Popover';
import { findChildByType } from '@/components/ui/ui.utils';
import { twMerge } from '@/utils';
import {
  ButtonProps,
  buttonVariants,
  containerVariants,
  DropdownContentComponent,
  ButtonContentComponent,
} from './Button.utils';

const dropdownButtonVariants = tv({
  base: [
    'tw-flex tw-items-center tw-justify-center',
    'tw-h-full',
    'tw-px-2 tw-mr-0',
    'tw-border-0 tw-rounded-r',
  ],
  variants: {
    disabled: {
      false: 'hover:!tw-bg-light-grey tw-text-tci-purple tw-cursor-pointer',
      true: 'tw-cursor-not-allowed tw-text-grey',
    },
  },
});

interface ButtonWithAdjacentDropdownProps extends ButtonProps {
  onButtonClick: () => void;
}

const ButtonWithAdjacentDropdown = ({
  ariaLabel,
  children,
  className,
  disabled = false,
  dropdownContent,
  onButtonClick,
  ...props
}: ButtonWithAdjacentDropdownProps) => {
  const dropdownContentOverride = findChildByType(children, ButtonWithAdjacentDropdown.DropdownContent);
  const buttonContentOverride = findChildByType(children, ButtonWithAdjacentDropdown.ButtonContent);

  return (
    <Tooltip.Provider>
      <Tooltip delayDuration={0}>
        <Popover>
          <Tooltip.Trigger asChild>
            <div className={containerVariants({ dropdownButtonLocation: 'outside' })}>
              <Popover.Anchor />
              <button
                className={buttonVariants({ disabled, dropdownButtonLocation: 'outside' })}
                disabled={disabled}
                type="button"
                onClick={onButtonClick}
                {...props}
              >
                <ButtonContentComponent {...buttonContentOverride?.props} />
              </button>
              <Popover.Trigger asChild>
                <button
                  aria-label={ariaLabel ?? 'Open dropdown menu'}
                  className={dropdownButtonVariants({ disabled })}
                  disabled={disabled}
                  type="button"
                >
                  <i className="fa fa-caret-down" />
                </button>
              </Popover.Trigger>
            </div>
          </Tooltip.Trigger>
          <Popover.Content
            align="start"
            className={twMerge(
              'tw-px-0 tw-my-0.5 tw-w-40',
              dropdownContentOverride?.props?.className,
            )}
            side="bottom"
            sideOffset={0}
            onCloseAutoFocus={e => e.preventDefault()}
          >
            <DropdownContentComponent {...dropdownContentOverride?.props} />
          </Popover.Content>
          <Tooltip.Content showArrow theme="white">
            {ariaLabel}
          </Tooltip.Content>
        </Popover>
      </Tooltip>
    </Tooltip.Provider>
  );
};

ButtonWithAdjacentDropdown.DropdownContent = DropdownContentComponent;
ButtonWithAdjacentDropdown.ButtonContent = ButtonContentComponent;
ButtonWithAdjacentDropdown.Close = Popover.Close;

export default ButtonWithAdjacentDropdown;
